// Eventos de anúncio do site público. As tags (gtag/fbq) e os IDs validados
// chegam no <head> renderizado pelo backend (app/services/tracking_tags.py);
// aqui só disparamos eventos quando o tenant configurou cada plataforma e o
// visitante consentiu (LGPD). O <head> já parte de consentimento negado.
// O funil próprio (siteEvents) não usa cookie nem dado pessoal e sai sempre.

import { sendSiteEvent } from './siteEvents';

interface TrackingConfig {
  googleTagId?: string;
  whatsappConversion?: string;
  metaPixelId?: string;
  consentRequired?: boolean;
}

export type ConsentDecision = 'granted' | 'denied';

type TrackingWindow = Window & {
  __TRACKING__?: TrackingConfig;
  // Espelho em memória da decisão (preenchido também pelo <head>): vale na
  // sessão quando o localStorage não está disponível.
  __COOKIE_CONSENT__?: ConsentDecision | null;
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
};

export type WhatsAppSource =
  | 'floating_button'
  | 'property_card'
  | 'property_detail'
  | 'broker_contact'
  | 'about_broker'
  | 'location_page'
  | 'lead_form';

export type LeadSource = 'contact_form' | 'property_form' | 'advertise_form';

const getConfig = (): TrackingConfig => (window as TrackingWindow).__TRACKING__ || {};

const safeCall = (fn: (() => void) | undefined) => {
  try {
    fn?.();
  } catch {
    // Bloqueador de anúncio ou script fora do ar não pode quebrar a navegação.
  }
};

const callGtag = (...args: unknown[]) => {
  const w = window as TrackingWindow;
  if (getConfig().googleTagId && w.gtag) safeCall(() => w.gtag!(...args));
};

const callFbq = (...args: unknown[]) => {
  const w = window as TrackingWindow;
  if (getConfig().metaPixelId && w.fbq) safeCall(() => w.fbq!(...args));
};

export const CONSENT_STORAGE_KEY = 'cookie_consent_v1';
export const CONSENT_OPEN_EVENT = 'cookie-consent:open';

const isDecision = (value: unknown): value is ConsentDecision =>
  value === 'granted' || value === 'denied';

export const getConsent = (): ConsentDecision | null => {
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (isDecision(stored)) return stored;
  } catch {
    // Modo privado/cookies bloqueados: cai no espelho em memória.
  }
  const inMemory = (window as TrackingWindow).__COOKIE_CONSENT__;
  return isDecision(inMemory) ? inMemory : null;
};

// O backend avisa quando a trava LGPD está desligada (COOKIE_CONSENT_REQUIRED).
export const isConsentRequired = () => getConfig().consentRequired !== false;

export const hasConsent = () => !isConsentRequired() || getConsent() === 'granted';

export const hasTrackingTags = () => {
  const { googleTagId, metaPixelId } = getConfig();
  return Boolean(googleTagId || metaPixelId);
};

const saveConsent = (decision: ConsentDecision) => {
  (window as TrackingWindow).__COOKIE_CONSENT__ = decision;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, decision);
  } catch {
    // Sem armazenamento a escolha vale só nesta sessão.
  }
};

const consentState = (value: ConsentDecision) => ({
  ad_storage: value,
  ad_user_data: value,
  ad_personalization: value,
  analytics_storage: value,
});

const ADVERTISING_COOKIE = /^(_fbp|_fbc|_ga|_ga_.+|_gid|_gcl_.+)$/;

// As tags gravam no domínio raiz (ex.: .ilaura.com.br); tenta cada nível.
const clearAdvertisingCookies = () => {
  const labels = window.location.hostname.split('.');
  const domains = [''].concat(labels.slice(0, -1).map((_, i) => `; domain=.${labels.slice(i).join('.')}`));
  document.cookie
    .split(';')
    .map((entry) => entry.split('=')[0].trim())
    .filter((name) => ADVERTISING_COOKIE.test(name))
    .forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain}`;
      });
    });
};

export const grantConsent = () => {
  saveConsent('granted');
  callGtag('consent', 'update', consentState('granted'));
  callFbq('consent', 'grant');
};

export const denyConsent = () => {
  saveConsent('denied');
  callGtag('consent', 'update', consentState('denied'));
  callFbq('consent', 'revoke');
  safeCall(clearAdvertisingCookies);
};

export const openConsentPreferences = () => {
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
};

// Eventos só saem com consentimento explícito, independente do Consent Mode.
const gtag = (...args: unknown[]) => {
  if (hasConsent()) callGtag(...args);
};

const fbq = (...args: unknown[]) => {
  if (hasConsent()) callFbq(...args);
};

const UNTRACKED_PREFIXES = ['/admin', '/superadmin', '/login'];

export const isTrackedPath = (pathname: string) =>
  !UNTRACKED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

// O GA4 (G-/GT-) já mede a navegação por histórico sozinho; só as tags do
// Ads (AW-) precisam do page_view manual para alimentar o remarketing.
const adsTagIds = (): string[] => {
  const { googleTagId, whatsappConversion } = getConfig();
  const ids = [googleTagId, whatsappConversion?.split('/')[0]];
  return Array.from(new Set(ids.filter((id): id is string => Boolean(id?.startsWith('AW-')))));
};

export const trackPageView = (path: string) => {
  const sendTo = adsTagIds();
  if (sendTo.length > 0) {
    gtag('event', 'page_view', {
      send_to: sendTo,
      page_path: path,
      page_location: `${window.location.origin}${path}`,
    });
  }
  fbq('track', 'PageView');
};

// O clique abre o WhatsApp (app ou outra aba) e a página pode ser descartada
// logo em seguida: beacon garante que a conversão sai mesmo assim.
export const trackWhatsAppClick = (source: WhatsAppSource) => {
  sendSiteEvent('whatsapp_click', { whatsappSource: source });
  gtag('event', 'whatsapp_click', { event_label: source, transport_type: 'beacon' });
  const { whatsappConversion } = getConfig();
  if (whatsappConversion) {
    gtag('event', 'conversion', { send_to: whatsappConversion, transport_type: 'beacon' });
  }
  fbq('track', 'Contact', { content_name: source });
};

export const trackLead = (source: LeadSource) => {
  sendSiteEvent('lead');
  gtag('event', 'generate_lead', { event_label: source });
  fbq('track', 'Lead', { content_name: source });
};
